import React, { useCallback, useEffect, useState } from 'react'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { useGetPresetByIdQuery } from '../../../../store/ai/ai'
import { useGlobalFormPresetInputParameters } from '../../AIFeaturePresets/PresetConfig/hooks'
import { PresetConfigLoader } from '../../AIFeaturePresets/PresetConfig/Common/PresetConfigLoader'
import { DISCUSSION_TOPICS_PRESET_IDS } from '../../../MeetingSummary/CRM/PresetMappings'
import { env } from '../../../../common/util'
import { useInputParametersUpdateScope } from '../../../../components/InputParameterHierarchy/hooks'
import { InputParameterConfigForm } from '../../../../components/InputParameterHierarchy/InputParameterConfigForm'
import { Button } from '../../../../components/Button/Button'

export const ConversationInsightsSettings = () => {
  const accountId = useAppSelector(selectAccountId)

  const { data: presetResponse } = useGetPresetByIdQuery({
    accountId,
    presetId: DISCUSSION_TOPICS_PRESET_IDS[env],
    includeRestricted: true,
  })
  const { presetInputParameters, updateInputParameters, updateLoading } = useGlobalFormPresetInputParameters(presetResponse?.data)
  const { hasAiFeatureUpdateScope } = useInputParametersUpdateScope()

  const [displayedInputParameters, setDisplayedInputParameters] = useState<Record<string, unknown> | null>(null)
  const [isChanged, setChanged] = useState<boolean>(false)

  useEffect(() => {
    setDisplayedInputParameters(presetInputParameters ?? {})
  }, [presetInputParameters])

  const handleClickCancel = () => {
    setDisplayedInputParameters(presetInputParameters ?? {})
  }

  const handleClickSave = useCallback(async () => {
    if (!displayedInputParameters) {
      return
    }
    setChanged(false)
    await updateInputParameters(displayedInputParameters)
  }, [displayedInputParameters, updateInputParameters])

  useEffect(() => {
    setChanged(JSON.stringify(displayedInputParameters) !== JSON.stringify(presetInputParameters))
  }, [displayedInputParameters, presetInputParameters])

  if (!presetResponse?.data || !presetInputParameters || !displayedInputParameters) {
    return <PresetConfigLoader />
  }

  return (
    <div className='d-flex flex-column zocks-gap-2.5'>
      <h4>Discussion Topics</h4>
      <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
        <i className='fs-5 bi bi-info-square zocks-text-primary' />
        <p className='d-flex flex-column paragraph-medium m-0'>
          <span className='zocks-text-gray-900'>These settings apply to all meetings in your account.</span>
        </p>
      </div>
      <div className='d-flex gap-3'>
        <ScopedBlock
          scope={{
            scope: 'account:update',
            superScope: ['account:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <div className='d-flex flex-column gap-3 w-100'>
            <InputParameterConfigForm
              inputParametersSchema={presetResponse.data.inputParametersSchema}
              inputParameters={displayedInputParameters}
              showCustomizationSetting={false}
              customized={false}
              onChange={setDisplayedInputParameters}
              disabled={!hasAiFeatureUpdateScope}
            />
            <div className='w-100 d-flex justify-content-between zocks-mt-3'>
              {hasAiFeatureUpdateScope && (
                <div className='d-flex gap-2'>
                  <Button variant='primary' onClick={handleClickSave} disabled={!isChanged} loading={updateLoading}>
                    Save
                  </Button>
                  <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading}>
                    Discard
                  </Button>
                </div>
              )}
            </div>
          </div>
        </ScopedBlock>
      </div>
    </div>
  )
}
